import stageKey from "Utils/stageKey";

const BLOCKED = ["OPTED_OUT", "BOUNCED", "DO_NOT_CONTACT"];
const MIN_ROWS = 10;
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
const pctOf = (n, d) => Math.round((n / d) * 100);
const hasActiveSequence = r =>
  (r.taggedSequences || []).some(s => s?.contactStatusInSequence === "ACTIVE");

const contactFindings = ({
  selection = null,
  totalContacts = 0,
  rows = [],
  now = Date.now(),
  hasDialerActivity = false,
  actions = {}
} = {}) => {
  const out = [];
  const list = Array.isArray(rows) ? rows : [];
  if (selection && selection.ids?.length) {
    const picked = Array.isArray(selection.rows) ? selection.rows : [];
    if (selection.withoutEmail > 0) {
      out.push({
        tone: "bad",
        key: "no_email",
        label: `${selection.withoutEmail} selected have no email`,
        text: "They cannot be sequenced. Enrich them first.",
        actions: actions.enrich ? [{ label: "Enrich selected", run: actions.enrich }] : []
      });
    }
    if ((selection.stages || []).some(s => BLOCKED.includes(stageKey(s)))) {
      out.push({
        tone: "warn",
        key: "blocked_selected",
        label: "Opted-out contacts selected",
        text: "Some of these opted out or bounced. Sending to them risks your domain.",
        actions: actions.deselectBlocked ? [{ label: "Remove them", run: actions.deselectBlocked }] : []
      });
    }
    const noOwner = picked.filter(r => !r.owner).length;
    if (noOwner) {
      out.push({
        tone: "warn",
        key: "no_owner_selected",
        label: `${noOwner} selected have no owner`,
        text: "Unowned contacts get no follow-up. Assign them.",
        actions: actions.assignOwner ? [{ label: "Assign owner", run: actions.assignOwner }] : []
      });
    }
    const missing = picked.filter(r => !r.jobTitle || !r.company).length;
    if (missing) {
      out.push({
        tone: "neutral",
        key: "missing_fields_selected",
        label: `${missing} selected missing title or company`,
        text: "Personalisation tokens will send blank. Enrich before sequencing.",
        actions: actions.enrich ? [{ label: "Enrich selected", run: actions.enrich }] : []
      });
    }
    out.push({
      tone: "neutral",
      key: "selected",
      label: `${selection.total || selection.ids.length} selected`,
      text: "Add them to a sequence, tag them, or ask me to compare them to a sequence.",
      actions: actions.addToSequence ? [{ label: "Add to sequence", run: actions.addToSequence }] : []
    });
    return out;
  }
  if (list.length) {
    const hotIdleRows = list.filter(
      r => (r.insights?.openCount || 0) + (r.insights?.clickCount || 0) >= 2 && !hasActiveSequence(r)
    );
    if (hotIdleRows.length) {
      out.push({
        tone: "hot",
        key: "hot_idle",
        label: `${hotIdleRows.length} hot but idle`,
        text: "They opened or clicked at least twice and are in no active sequence.",
        actions: actions.selectRows ? [{ label: "Show them", run: () => actions.selectRows(hotIdleRows) }] : []
      });
    }

    if (list.length >= MIN_ROWS) {
      const outsideRows = list.filter(r => !hasActiveSequence(r));
      const outsidePct = pctOf(outsideRows.length, list.length);
      if (outsidePct >= 20) {
        out.push({
          tone: "warn",
          key: "outside_sequence",
          label: `${outsidePct}% sitting outside any sequence`,
          text: "Saved but nobody is reaching out.",
          actions: actions.addToSequence
            ? [
                {
                  label: "Add to sequence",
                  run: () => {
                    actions.selectRows && actions.selectRows(outsideRows);
                    actions.addToSequence();
                  }
                }
              ]
            : []
        });
      }

      // emailStatus is the standard verification status ("valid", "invalid"…), or empty when the
      // email was never checked. Only speak up once some of these contacts have been checked.
      const isValid = r => String(r.emailStatus || "").toLowerCase() === "valid";
      if (list.some(r => r.emailStatus)) {
        const unverifiedRows = list.filter(r => !isValid(r));
        const unverifiedPct = pctOf(unverifiedRows.length, list.length);
        if (unverifiedPct >= 25) {
          out.push({
            tone: "neutral",
            key: "missing_verified_email",
            label: `${unverifiedPct}% missing a verified email`,
            text: "Enrich them before sequencing; I will show the credit cost first.",
            actions: actions.enrich
              ? [
                  {
                    label: "Enrich",
                    run: () => {
                      actions.selectRows && actions.selectRows(unverifiedRows);
                      actions.enrich();
                    }
                  }
                ]
              : []
          });
        }
      }

      if (hasDialerActivity) {
        const noMobileRows = list.filter(r => !r.mobilePhone);
        const noMobilePct = pctOf(noMobileRows.length, list.length);
        if (noMobilePct >= 50) {
          out.push({
            tone: "neutral",
            key: "mobiles_unrevealed",
            label: `${noMobilePct}% have no mobile`,
            text: "You call; these have no number. Reveal phones, cost shown first.",
            actions: actions.revealPhones ? [{ label: "Reveal phones", run: actions.revealPhones }] : []
          });
        }
      }
    }

    const optedOutRows = list.filter(r => stageKey(r.stage) === "OPTED_OUT");
    if (optedOutRows.length) {
      out.push({
        tone: "neutral",
        key: "optouts_present",
        label: "Opt-outs in this view",
        text: "They cannot be contacted. Filter them out.",
        actions: actions.filterOptOuts ? [{ label: "Filter them out", run: () => actions.filterOptOuts(optedOutRows) }] : []
      });
    }

    const seen = new Map();
    list.forEach(r => {
      const e = String(r.email || "").toLowerCase();
      if (e) seen.set(e, (seen.get(e) || 0) + 1);
    });
    const dupes = [...seen.values()].filter(n => n > 1).length;
    if (dupes) {
      out.push({
        tone: "neutral",
        key: "duplicates",
        label: plural(dupes, "possible duplicate"),
        text: "Same email more than once on this page. Merge before they get emailed twice.",
        actions: actions.duplicates ? [{ label: "Find duplicates", run: actions.duplicates }] : []
      });
    }
  }
  const hadRowFindings = out.length;
  if (totalContacts > 20) {
    out.push({
      tone: "neutral",
      key: "describe",
      label: "Filter by describing them",
      text: "Tell me who you want, for example VPs who opened but never replied, and I will find them.",
      actions: actions.describe ? [{ label: "Describe them", run: actions.describe }] : []
    });
  }
  if (totalContacts > 0 && !hadRowFindings) {
    out.push({
      tone: "neutral",
      key: "base",
      label: `${totalContacts} contacts`,
      text: "Select some and I can enrich, sequence or clean them."
    });
  }
  return out;
};

export default contactFindings;
